import React, { useState, useEffect } from 'react';
import api from '../../../api/axios';

// Compact MedicalTestList Component - Optimized for 100+ tests
const MedicalTestList = ({ tests, onSelectTest, selectedTests = [], onViewDetails, loading, error }) => {
  const [fetchedTests, setFetchedTests] = useState([]);
  const [isLoading, setIsLoading] = useState(false); // Changed: Start with false
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [hasFetched, setHasFetched] = useState(false); // Added: Track if we've already fetched

  useEffect(() => {
    // Only fetch if tests are not provided via props AND we haven't fetched yet
    if ((!tests || tests.length === 0) && !hasFetched) {
      fetchTests();
    }
  }, [tests, hasFetched]);

  const fetchTests = async () => {
    if (isLoading) return; // Prevent multiple simultaneous requests
    
    try {
      setIsLoading(true);
      setFetchError(null); // Clear any previous errors
      
      const response = await api.get('/medical-tests/');
      
      // Removed the artificial delay - this might be causing issues
      setFetchedTests(response.data);
      setHasFetched(true); // Mark as fetched
      setIsLoading(false);
    } catch (err) {
      setFetchError('Failed to fetch medical tests');
      console.error('Error fetching tests:', err);
      setIsLoading(false);
      setHasFetched(false); // Allow retry
    }
  };

  // Use props data if provided, otherwise use fetched data
  // Ensure we always have an array to work with
  const displayTests = Array.isArray(tests) && tests.length > 0 
    ? tests 
    : Array.isArray(fetchedTests) 
      ? fetchedTests 
      : [];
  
  const filteredTests = displayTests.filter(test => {
    // Add safety check for test object
    if (!test || typeof test !== 'object') return false;
    
    const matchesSearch = test.title && test.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || test.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const isSelected = (testId) => {
    return Array.isArray(selectedTests) && selectedTests.some(test => test && test.id === testId);
  };

  const handleTestClick = (test, event) => {
    if (event.target.closest('.select-button')) {
      onSelectTest && onSelectTest(test);
    } else {
      onViewDetails && onViewDetails(test.id);
    }
  };

  // Show loading only when actually loading (not when we have data)
  const shouldShowLoading = (loading || isLoading) && displayTests.length === 0;

  if (shouldShowLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="relative">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 bg-blue-600 rounded-full animate-pulse opacity-75"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || fetchError) {
    return (
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <p className="text-red-600 mb-4 text-lg">{error || fetchError}</p>
        <button 
          onClick={() => {
            setHasFetched(false); // Reset fetch flag
            fetchTests();
          }}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-xl font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Filter Section */}
      <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search tests by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="md:w-48">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="block w-full px-3 py-3 border border-gray-200 bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="blood">Blood Tests</option>
              <option value="hormone">Hormone Tests</option>
              <option value="organ">Organ Function</option>
              <option value="cardiac">Cardiac Tests</option>
              <option value="diabetes">Diabetes Tests</option>
              <option value="liver">Liver Function</option>
              <option value="kidney">Kidney Function</option>
            </select>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="mt-4 text-center">
          <span className="text-sm text-gray-600 bg-white px-3 py-1 rounded-full border">
            {filteredTests.length} tests found
          </span>
        </div>
      </div>

      {/* Loading indicator for when we have data but are refreshing */}
      {(loading || isLoading) && displayTests.length > 0 && (
        <div className="flex justify-center py-2">
          <div className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2">
            <div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            Updating...
          </div>
        </div>
      )}

      {/* Compact Tests Grid - Optimized for many tests */}
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {filteredTests.map((test) => (
          <div 
            key={test.id}
            className={`group relative bg-white rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 border-2 ${
              isSelected(test.id) 
                ? 'border-blue-500 shadow-md bg-gradient-to-br from-blue-50 to-indigo-50 ring-2 ring-blue-100' 
                : 'border-gray-100 shadow-sm hover:border-blue-200'
            }`}
            onClick={(e) => handleTestClick(test, e)}
          >
            {/* Selection Indicator */}
            <div 
              className="select-button absolute top-2 right-2 z-10"
              onClick={(e) => e.stopPropagation()}
            >
              {isSelected(test.id) ? (
                <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-sm">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : (
                <button 
                  className="w-6 h-6 border-2 border-gray-300 rounded-full hover:border-blue-500 hover:scale-110 transition-all duration-200"
                  onClick={() => onSelectTest && onSelectTest(test)}
                />
              )}
            </div>

            {/* Test Content - Compact Layout */}
            <div className="pr-6 mb-3">
              <h3 className="font-semibold text-sm text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 leading-tight">
                {test.title}
              </h3>
            </div>

            {/* Test Info Tags - Smaller */}
            <div className="flex flex-wrap gap-1 mb-3">
              {test.fasting && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full font-medium">
                  Fasting
                </span>
              )}
              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full font-medium">
                {test.turnaround || '24-48h'}
              </span>
            </div>
            
            {/* Compact Pricing Section */}
            <div className="flex flex-col">
              <div className="flex items-center justify-between">
                {test.offer_price ? (
                  <div className="flex flex-col">
                    <span className="text-lg font-bold text-green-600">₹{test.offer_price}</span>
                    <span className="text-xs text-gray-500 line-through">₹{test.price}</span>
                  </div>
                ) : (
                  <span className="text-lg font-bold text-gray-900">₹{test.price}</span>
                )}
                {test.offer_price && (
                  <div className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
                    {Math.round(((test.price - test.offer_price) / test.price) * 100)}%
                  </div>
                )}
              </div>
            </div>

            {/* Hover indicator for view details */}
            <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded-lg font-medium shadow-lg">
                View Details
              </div>
            </div>

            {/* Hover Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
          </div>
        ))}
      </div>

      {/* Load More Button for large datasets */}
      {filteredTests.length > 50 && (
        <div className="text-center pt-6">
          <button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-medium transition-all duration-300 shadow-lg hover:shadow-xl">
            Load More Tests
          </button>
        </div>
      )}

      {filteredTests.length === 0 && !isLoading && !loading && (
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <p className="text-gray-500 text-lg">No tests found matching your criteria</p>
          <p className="text-gray-400 text-sm mt-2">Try adjusting your search or filter</p>
        </div>
      )}
    </div>
  );
};

export default MedicalTestList;